import React, { useState } from "react";
import { useGetProductReviewsAdminQuery, useUpdateProductReviewMutation, useReplyToReviewMutation } from "Services/Admin/ProductReviewAdminApiSlice";
import { useCustomerQuery } from "Services/CustomerApiSlice";

interface ProductReviewTableProps {
  productId: number;
}

const ProductReviewTable: React.FC<ProductReviewTableProps> = ({
  productId,
}) => {
  const plant = process.env.REACT_APP_PLANT || "plant";

  const { data: customerData } = useCustomerQuery();

  const customerNo = customerData?.results?.customerNo;


  const { data, isLoading } = useGetProductReviewsAdminQuery(
    { plant, productId },
    { skip: !productId }
  );

  const [updateReview] = useUpdateProductReviewMutation();
  const [replyToReview, { isLoading: isReplying }] = useReplyToReviewMutation();

  const [openReplyId, setOpenReplyId] = useState<number | null>(null);
  const [replyText, setReplyText] = useState("");

  const reviews = Array.isArray((data as any)?.results?.reviews)
  ? (data as any).results.reviews.map((item: any) => ({
      id: item.reviewHdr.id,
      productId: item.reviewHdr.productId,
      customerId: item.reviewHdr.customerId,
      customerName: item.reviewHdr.customerName,
      rating: item.reviewHdr.rating,
      reviewComment: item.reviewHdr.reviewComment,
      showHide: item.reviewHdr.showHide,
      createdDate: item.reviewHdr.createdAt,
      adminReply:
        Array.isArray(item.reviewDets) && item.reviewDets.length > 0
          ? item.reviewDets[0].replyComment
          : "",
    }))
  : [];


  const handleToggleShowHide = async (review: any) => {
    try {
      await updateReview({
        id: review.id,
        plant,
        productId: String(review.productId),
        customerId: String(review.customerId),
        rating: review.rating,
        reviewComment: review.reviewComment,
        showHide: review.showHide === 1 ? 0 : 1,
      }).unwrap();
    } catch (err) {
      console.error("Failed to update review:", err);
    }
  };

  const handleReplySubmit = async (review: any) => {
    if (!replyText.trim()) return;

    try {
      await replyToReview({
        id: review.id,
        plant,
        customerId: String(review.customerId),
        replyComment: replyText.trim(),
      }).unwrap();

      setOpenReplyId(null);
      setReplyText("");
    } catch (err) {
      console.error("Failed to reply:", err);
    }
  };

  if (isLoading) {
    return (
      <div className="mt-6 text-sm text-gray-500">
        Loading product reviews...
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className="mt-6 text-sm text-gray-400">
        No reviews found for this product
      </div>
    );
  }

  return (
    <div className="mt-8 bg-white rounded-lg border shadow-sm p-4">
  <h3 className="text-md font-semibold mb-3">Product Reviews & Ratings</h3>

  {/*  ADD THIS WRAPPER */}
  <div className="w-full overflow-x-auto">
    <table className="min-w-[900px] w-full text-sm border-collapse">
      <thead>
        <tr className="border-b text-left text-gray-600">
          <th className="py-2 whitespace-nowrap">Customer</th>
          <th className="py-2 whitespace-nowrap">Rating</th>
          <th className="py-2">Review</th>
          <th className="py-2 whitespace-nowrap">Show / Hide</th>
          <th className="py-2 whitespace-nowrap">Date</th>
          <th className="py-2 whitespace-nowrap">Reply</th>
        </tr>
      </thead>

      <tbody>
        {reviews.map((review: any) => (
          <React.Fragment key={review.id}>
            <tr className="border-b align-top">
              <td className="py-2 min-w-[200px]">
                {review.customerName || review.customerId || "Guest Customer"}
                <br />
                <small className="text-xs text-gray-500">
                  Current User: {customerNo || "Not logged in"}
                </small>
              </td>

              <td className="py-2 whitespace-nowrap">
                {review.rating} ★
              </td>

              <td className="py-2 min-w-[260px]">
                {review.reviewComment}
                {review.adminReply && (
                  <div className="mt-2 text-xs text-green-700 bg-green-50 p-2 rounded">
                    <strong>Admin reply:</strong> {review.adminReply}
                  </div>
                )}
              </td>

              <td className="py-2 whitespace-nowrap">
                <button
                  onClick={() => handleToggleShowHide(review)}
                  className={`px-2 py-1 rounded text-xs font-medium ${
                    review.showHide === 1
                      ? "bg-green-100 text-green-700"
                      : "bg-gray-200 text-gray-600"
                  }`}
                >
                  {review.showHide === 1 ? "Visible" : "Hidden"}
                </button>
              </td>

              <td className="py-2 whitespace-nowrap text-gray-500">
                {review.createdDate
                  ? new Date(review.createdDate).toLocaleDateString()
                  : "-"}
              </td>

              <td className="py-2 whitespace-nowrap">
                {review.adminReply ? (
                  <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded">
                    Replied
                  </span>
                ) : (
                  <button
                    onClick={() => {
                      setOpenReplyId(review.id);
                      setReplyText("");
                    }}
                    className="text-blue-600 text-xs underline"
                  >
                    Reply
                  </button>
                )}
              </td>
            </tr>

            {openReplyId === review.id && (
              <tr className="bg-gray-50">
                <td colSpan={6} className="p-3">
                  <textarea
                    className="w-full border rounded p-2 text-sm"
                    rows={3}
                    placeholder="Write admin reply..."
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                  />
                  <div className="flex justify-end gap-2 mt-2">
                    <button
                      onClick={() => {
                        setOpenReplyId(null);
                        setReplyText("");
                      }}
                      className="px-3 py-1 text-xs border rounded"
                    >
                      Cancel
                    </button>
                    <button
                      disabled={isReplying}
                      onClick={() => handleReplySubmit(review)}
                      className="px-3 py-1 text-xs bg-blue-600 text-white rounded disabled:opacity-60"
                    >
                      {isReplying ? "Saving..." : "Submit Reply"}
                    </button>
                  </div>
                </td>
              </tr>
            )}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  </div>
</div>

  );
};

export default ProductReviewTable;
